import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary:
    'bg-gradient-to-b from-primary-700 to-primary-800 text-white shadow-pop hover:from-primary-600 hover:to-primary-700 hover:shadow-card-hover focus-visible:ring-primary-300',
  secondary:
    'bg-white text-primary-800 border border-border hover:bg-surface-muted hover:border-primary-200 focus-visible:ring-primary-200',
  ghost: 'bg-transparent text-ink-muted hover:bg-surface-muted focus-visible:ring-primary-200',
  danger: 'bg-gradient-to-b from-error to-red-700 text-white shadow-pop hover:opacity-95 focus-visible:ring-red-300',
};

const sizeClasses: Record<Size, string> = {
  sm: 'text-[12.5px] px-3 py-1.5 gap-1.5',
  md: 'text-[13.5px] px-4 py-2 gap-2',
  lg: 'text-[14.5px] px-5 py-2.5 gap-2',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading = false, disabled, className = '', children, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={`inline-flex items-center justify-center rounded-xl font-semibold whitespace-nowrap
          transition-all duration-200 ease-premium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1
          disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none cursor-pointer active:scale-[0.98]
          ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
        {...rest}
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
